var _excluded = ["borderless"];
var _templateObject, _templateObject2;
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function _taggedTemplateLiteral(strings, raw) { if (!raw) { raw = strings.slice(0); } return Object.freeze(Object.defineProperties(strings, { raw: { value: Object.freeze(raw) } })); }
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Popup as KendoPopup } from '@progress/kendo-react-popup';
var StyledPopup = styled(_ref => {
  var {
      borderless
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(KendoPopup, rest);
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    ", "\n"])), props => props.borderless && css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n        .k-popup {\n            border: none;\n        }\n    "]))));

/**
 * The KendoReact Popup positions a piece of content next to a specific anchor component.
 */
var Popup = props => /*#__PURE__*/React.createElement(StyledPopup, props);
Popup.defaultProps = {
  anchor: undefined,
  anchorAlign: undefined,
  animate: undefined,
  appendTo: undefined,
  borderless: false,
  className: undefined,
  collision: undefined,
  id: undefined,
  margin: undefined,
  offset: undefined,
  onClose: undefined,
  onOpen: undefined,
  onPosition: undefined,
  popupAlign: undefined,
  popupClass: undefined,
  positionMode: undefined,
  scale: undefined,
  show: undefined,
  style: undefined
};
Popup.propTypes = {
  /**
   * Specifies the element which will be used as an anchor. The Popup opens next to that element.
   */
  anchor: PropTypes.instanceOf(HTMLElement),
  /**
   * Specifies the pivot point of the anchor.
   */
  anchorAlign: PropTypes.shape({
    horizontal: PropTypes.oneOf(['center', 'left', 'right']),
    vertical: PropTypes.oneOf(['center', 'top', 'bottom'])
  }),
  /**
   * Controls the Popup animation. By default, the opening and closing animations are enabled.
   */
  animate: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    closeDuration: PropTypes.number,
    openDuration: PropTypes.number
  })]),
  /**
   * Defines the container to which the Popup will be appended. Defaults to body.
   * If set to null the Popup will be rendered without React Portal.
   */
  appendTo: PropTypes.instanceOf(HTMLElement),
  /**
   * Determines if the Popup should have a border or not.
   */
  borderless: PropTypes.bool,
  /**
   * Specifies a list of CSS classes that will be added to the Popup element.
   */
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * Configures the collision behavior of the Popup.
   */
  collision: PropTypes.shape({
    horizontal: PropTypes.oneOf(['flip', 'fit']),
    vertical: PropTypes.oneOf(['flip', 'fit'])
  }),
  /**
   * Specifies the id that will be added to the Popup element.
   */
  id: PropTypes.string,
  /**
   * Configures the margin value that will be added to the popup dimensions in pixels and leaves
   * a blank space between the popup and the anchor.
   */
  margin: PropTypes.shape({
    horizontal: PropTypes.number,
    vertical: PropTypes.number
  }),
  /**
   * Specifies the absolute position of the element. The Popup opens next to that point.
   * The pivot point of the Popup is defined by the popupAlign configuration option.
   * The boundary detection is applied by using the window viewport.
   */
  offset: PropTypes.shape({
    left: PropTypes.number,
    top: PropTypes.number
  }),
  /**
   * Fires after the Popup is closed.
   */
  onClose: PropTypes.func,
  /**
   * Fires after the Popup is opened and the opening animation ends.
   */
  onOpen: PropTypes.func,
  /**
   * Fires after the Popup position is set.
   */
  onPosition: PropTypes.func,
  /**
   * Specifies the pivot point of the Popup.
   */
  popupAlign: PropTypes.shape({
    horizontal: PropTypes.oneOf(['center', 'left', 'right']),
    vertical: PropTypes.oneOf(['center', 'top', 'bottom'])
  }),
  /**
   * Specifies a list of CSS classes that will be added to the internal animated element.
   */
  popupClass: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string), PropTypes.shape({})]),
  /**
   * Specifies the position mode of the component. By default, the Popup uses fixed positioning.
   * To make the Popup acquire absolute positioning, set this option to absolute.
   * If you need to support mobile browsers with the zoom option, use the absolute positioning
   * of the Popup.
   */
  positionMode: PropTypes.oneOf(['static', 'relative', 'absolute', 'fixed', 'sticky']),
  /**
   * Used to set the document scale when using a scale transform.
   * The document or container scale is required to compute the popup position correctly.
   * Detecting the scale is not reliable and must be set by providing a value for SCALE. See
   * Support for Document Scale. Using this token is not necessary for user-applied browser zoom.
   */
  scale: PropTypes.number,
  /**
   * Controls the Popup visibility.
   */
  show: PropTypes.bool,
  /**
   * Represents the styles that are applied to the Popup.
   */
  style: PropTypes.shape({})
};
export default Popup;